import type { DialerOffer, OfferedLead } from "@/lib/types";

/**
 * The dialer on the rep's phone (ADR-019): the words and the arithmetic of a preview offer, and when
 * a voicemail drop applies. Pure, for `test/dialer.test.ts`.
 */

/** Whole seconds until the offer goes back to the pool — rounded up, so "0" means it has ended —
 *  or null when it has no deadline the console can read. */
export function secondsLeft(expiresAt: string | null, now: number): number | null {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

/**
 * How often the lead has been called, and how the last call went: "First call", "Called once
 * before · No answer". `label` turns the last outcome's slug into the campaign's word for it; a slug
 * it does not know is shown as the server spelled it.
 */
export function attemptsText(
  offer: Pick<DialerOffer, "attempts" | "last_disposition">,
  label: (slug: string) => string | undefined,
): string {
  const { attempts, last_disposition: last } = offer;
  const times =
    attempts === 0
      ? "First call"
      : attempts === 1
        ? "Called once before"
        : `Called ${attempts} times before`;
  if (attempts === 0 || !last) return times;
  return `${times} · ${label(last) ?? last.replaceAll("_", " ")}`;
}

/**
 * The list's other columns worth a glance before dialing, in the file's order and at most `max` of
 * them. A value that is empty, or not text or a number, is left out rather than printed as
 * `[object Object]`.
 */
export function leadDetails(
  attributes: Record<string, unknown> | null,
  max = 6,
): [label: string, value: string][] {
  const rows: [string, string][] = [];
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (rows.length === max) break;
    if (typeof value !== "string" && typeof value !== "number") continue;
    const text = String(value).trim();
    if (text) rows.push([key.replaceAll("_", " "), text]);
  }
  return rows;
}

/** Where the lead is, for the clock that decides when they may be called: "CA · America/Los_Angeles · ES". */
export function leadPlace(lead: Pick<OfferedLead, "state" | "timezone" | "language">): string | null {
  const parts = [lead.state, lead.timezone, lead.language?.toUpperCase()].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** How long the phone keeps naming a lead it dialed: well past any dial timeout and ring. */
export const DIALED_LEAD_MS = 2 * 60_000;

/**
 * The lead the rep dialed from an offer, while that dial is recent enough to be the call the dialer
 * is placing. The offer has left the server by then. An old one must not name the next lead — in
 * power mode, say, which dials without offering anything.
 */
export function recentDialedLead(
  dialed: { offer: DialerOffer; at: number } | null,
  now: number,
): DialerOffer | null {
  return dialed !== null && now - dialed.at < DIALED_LEAD_MS ? dialed.offer : null;
}

/**
 * The library with the lead's own message first (ADR-061).
 *
 * core-api chooses it from the lead's stage and language; the phone only has to lead with it. The
 * rest of the library follows in its own order and is one click away, because a rep who has just
 * learned something on the call knows more than the stage does.
 */
export function voicemailOrder<T extends { slug: string }>(
  messages: readonly T[],
  suggested: string | null,
): T[] {
  if (!suggested) return [...messages];
  const first = messages.filter((m) => m.slug === suggested);
  return [...first, ...messages.filter((m) => m.slug !== suggested)];
}

/** A message can be left only on a connected call to a lead the dialer reached. */
export function canDropVoicemail(call: { state: string; kind: string | null } | null): boolean {
  return call !== null && call.state === "busy" && call.kind === "outbound";
}
